import { disputeRoundsOf, type DisputeRound } from '@shared/factory-dispute';
import { questionRounds, type QuestionRound } from '@shared/upstream-deferral';
import type { TaskEvent } from '@shared/types/tasks';
import { showFoldingCard } from './folding-card';

/**
 * One round of the AI builder's questions on a task (T-10), what came back (T-22), and what went
 * upstream (T-58): `sent` and `proposals`. A round with nothing sent closes on any comment from a
 * person; one with questions sent upstream only on an Answers message or the upstream wait being
 * removed (shared/upstream-deferral.ts, the rule the worker uses too).
 */
export type DecisionRound = QuestionRound<TaskEvent>;

/**
 * Every round of questions in a task's events (oldest first), newest round first. A round's answers
 * are the first Answers message a person posted after it, before the next round was asked (or the
 * factory's automatic one for it).
 */
export function decisionRounds(events: TaskEvent[]): DecisionRound[] {
  return questionRounds(events).reverse();
}

/**
 * SF-T-74: every disputed review on the task (a factory-dispute block in a run's handoff note), newest
 * first, with what people answered (disputeRoundsOf: the dispute card, or deciding a matching suggestion).
 */
export function taskDisputeRounds(events: TaskEvent[]): DisputeRound<TaskEvent>[] {
  // The factory's waiting_added lines say who the run handed the task to: a comment from them is their reply.
  return disputeRoundsOf(events.filter((e) => (e.kind === 'chat' && e.body) || e.kind === 'waiting_added')).reverse();
}

/** The id of a task's Decisions section (TaskDecisions). */
export const decisionsAnchorId = (taskId: string) => `task-decisions-${taskId}`;

/** The id of one round's card, wherever it shows (To answer while open, Decisions once answered). */
export const decisionRoundId = (eventId: string) => `task-decision-${eventId}`;

/** SF-T-74: the id of one disputed review's card, wherever it shows (To answer while open, Decisions once settled). */
export const disputeRoundId = (eventId: string) => `task-dispute-${eventId}`;

/** Scrolls to a round of questions or a disputed review (the pointer line in the comments), opening it if it's folded; else to the Decisions. */
export function showTaskDecisions(taskId: string, eventId?: string) {
  if (eventId && (showFoldingCard(decisionRoundId(eventId)) || showFoldingCard(disputeRoundId(eventId)))) return;
  document.getElementById(decisionsAnchorId(taskId))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
